import { Checkbox, Dropdown, Field, Input, makeStyles, Option, Text, tokens } from '@fluentui/react-components';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { AppDetailsHeader } from '@tdp/common/components/AppDetailsHeader';
import { makeDocsLink } from '@tdp/common/constants';

import { createAppZoomHook } from '../../../hooks';
import { appFeaturesCommonStyles } from '../AppFeaturesCommon/appFeaturesCommon.styles';
import { createAppFeaturesFooter } from '../AppFeaturesCommon/AppFeaturesFooter';

const useStyles = makeStyles({
  section: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
    maxWidth: '700px'
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalXXS,
  }
});


const useConfigurableTabs = createAppZoomHook(app => app.configurableTabs);
const Footer = createAppFeaturesFooter(app => [app.configurableTabs]);

export const AppFeaturesGroupTab: React.FC = () => {
  const navigate = useNavigate();
  const baseStyles = appFeaturesCommonStyles();
  const classes = useStyles();
  const intl = useIntl();

  const appId = useParams<{ appId: string }>().appId ?? '';
  const tabs = useConfigurableTabs(appId);
  const configTab = tabs.current?.[0]
  const isConfigUrlAbsent = !(tabs.current?.[0]?.configurationUrl)
  const [hasError, setHasError] = React.useState(false);


  const options = [
    "meetingSidePanel",
    "privateChatTab",
    "meetingChatTab",
    "channelTab",
    "meetingStage",
    "meetingDetailsTab",
  ]

  const onConfigUrlChange = (val: string) => {
    setHasError(!val.startsWith("https://"));
    tabs.update(t => [
      { ...configTab, configurationUrl: val, }
    ])
  }

  const onCanUpdateConfigurationChange = (val: boolean) => {
    tabs.update(t => [
      { ...configTab, canUpdateConfiguration: val }
    ])
  }

  const onScopeChanged = (str: string): void => {
    const scopes = [...(tabs.current?.[0]?.scopes ?? [])];
    scopes.includes(str) ? scopes.splice(scopes.indexOf(str), 1) : scopes.push(str);
    tabs.update(t => [
      { ...configTab, scopes: scopes }
    ])
  }

  const onSharePointIntegrationChange = (str: string): void => {
    const sharePoint = [...(tabs.current?.[0]?.supportedSharePointHosts ?? [])];
    sharePoint.includes(str) ? sharePoint.splice(sharePoint.indexOf(str), 1) : sharePoint.push(str);
    tabs.update(t => [
      { ...configTab, supportedSharePointHosts: sharePoint }
    ])
  }

  const onOptionSelect = (selectedOptions: string[]) => {
    tabs.update(t => [
      { ...configTab, context: selectedOptions }
    ])
  }

  console.log('!!tabs', !!(tabs.current?.[0]?.configurationUrl))

  return (
    <div className={baseStyles.wrapper}>
      <div className={baseStyles.container}>
        <AppDetailsHeader
          onBackNavigation={() => { navigate(-1); }}
          showBackNavigation
          title={<FormattedMessage
            defaultMessage="Group and channel app"
            description={'Title for Group and channel app Section'}
          />}
          description={<FormattedMessage
            defaultMessage="A space to display hosted app experiences (such as a list or dashboard) in team channels and group chats."
            description={'Description for Group and channel app Section'}
          />}
          link={<FormattedMessage
            defaultMessage="Learn more about Group and channel app"
            description={'Link to learn more about Group and channel app Section'}
          />}
          href={makeDocsLink("microsoftteams/platform/tabs/how-to/create-channel-group-tab")}
        />
        <br />

        <section className={classes.section}>
          <Text weight='bold' size={400}>
            <FormattedMessage
              defaultMessage="Configuration URL"
              description={'Title for Configuration URL'}
            />
          </Text>
          <Field label={intl.formatMessage({ defaultMessage: "Provide a URL for the app’s configuration dialog." })} required>
            <Input
              placeholder={intl.formatMessage({ defaultMessage: "https://example.domain.com" })}
              value={tabs.current?.[0]?.configurationUrl ?? ''}
              onChange={(e, data) => { onConfigUrlChange(data.value) }}
            />
            {hasError && (
              <Text className={classes.errorText}>
                <FormattedMessage
                  defaultMessage="Configuration URL must start with 'https://'."
                  description={'Error message for invalid configuration URL'}
                />
              </Text>
            )}
          </Field>
          <Checkbox
            label={intl.formatMessage({ defaultMessage: "Users can reconfigure the app" })}
            checked={tabs.current?.[0]?.canUpdateConfiguration ?? false}
            onChange={(e, data) => { onCanUpdateConfigurationChange(!!data.checked) }}
          />
        </section>

        <section className={classes.section}>
          <Text weight='bold' size={400}>
            <FormattedMessage
              defaultMessage="Scope"
              description={'Title for Scope'}
            />
          </Text>
          <>
            <Field label={intl.formatMessage({ defaultMessage: "Choose the meeting surfaces where people can use your app." })} />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: "Team" })}
              checked={tabs.current?.[0]?.scopes?.includes('team') ?? false}
              disabled={isConfigUrlAbsent}
              onChange={(e, data) => { onScopeChanged('team'); }}
            />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: "Group chat" })}
              checked={tabs.current?.[0]?.scopes?.includes('groupChat') ?? false}
              disabled={isConfigUrlAbsent}
              onChange={(e, data) => { onScopeChanged('groupChat'); }}
            />
          </>
        </section>

        <section className={classes.section}>
          <Text weight='bold' size={400}>
            <FormattedMessage
              defaultMessage="Context"
              description={'Title for context'}
            />
          </Text>
          <Field label={intl.formatMessage({ defaultMessage: "The set of contextItem scopes where the tab is supported." })}>
            <Dropdown
              multiselect
              placeholder={intl.formatMessage({ defaultMessage: "Select context" })}
              value={tabs.current?.[0]?.context?.join(', ') ?? ''}
              selectedOptions={[...(tabs.current?.[0]?.context ?? [])]}
              onOptionSelect={(e, data) => { onOptionSelect(data.selectedOptions) }}
              disabled={isConfigUrlAbsent}
            >
              {options.map((option) => (
                <Option key={option}>
                  {option}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </section>

        <section className={classes.section}>
          <Text weight='bold' size={400}>
            <FormattedMessage
              defaultMessage="SharePoint integration (if applicable)"
              description={'Title for sharepoint integration'}
            />
          </Text>
          <>
            <Field label={intl.formatMessage({ defaultMessage: "The set of contextItem scopes where the tab is supported." })} />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: "App can run as an app page (full-screen experience) in SharePoint." })}
              checked={tabs.current?.[0]?.supportedSharePointHosts?.includes('sharePointFullPage') ?? false}
              onChange={(e, data) => { onSharePointIntegrationChange('sharePointFullPage'); }}
              disabled={isConfigUrlAbsent}
            />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: "App can include SharePoint web parts." })}
              checked={tabs.current?.[0]?.supportedSharePointHosts?.includes('sharePointWebPart') ?? false}
              onChange={(e, data) => { onSharePointIntegrationChange('sharePointWebPart'); }}
              disabled={isConfigUrlAbsent}
            />
          </>
        </section>

      </div>
      <Footer isError={!(tabs.current?.[0]?.configurationUrl) || hasError} />
    </div>
  );
}
